import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { fromJS } from 'immutable';
import {
    makeSelectOwnUserIsLoggedInToQueue,
    makeSelectOwnUserIsMemberOfQueue,
    selectQueueMembers,
} from 'flow/selectors/queue-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { updateQueueMember } from 'flow/actions/pbx-actions';
import { QueueKey } from 'flow/types/EntityKeys';
import { AppDispatch } from 'flow/types/Redux';
import { State } from 'flow/types/State';
import PbxMemberLoggedInStatusItem from 'flow/components/pbx/PbxMemberLoggedInMemberStatus';
import Toggle from 'flow/components/ui-components/Toggle';

interface Props {
    queueKey: QueueKey;
}

const QueueOwnMemberLogin = ({ queueKey }: Props) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();

    const selectOwnUserIsMemberOfQueue = useMemo(
        makeSelectOwnUserIsMemberOfQueue,
        [],
    );
    const ownUserIsMemberOfQueue = useAppSelector((state) =>
        selectOwnUserIsMemberOfQueue(state, queueKey),
    );

    const selectOwnUserIsLoggedIntoQueue = useMemo(
        makeSelectOwnUserIsLoggedInToQueue,
        [],
    );
    const isLoggedInToQueue = useAppSelector((state) =>
        selectOwnUserIsLoggedIntoQueue(state, queueKey),
    );

    if (!ownUserIsMemberOfQueue) return null;

    return (
        <PbxMemberLoggedInStatusItem
            toggleButton={
                <Toggle
                    value={isLoggedInToQueue}
                    onChange={(loggedIn) => {
                        dispatch(updateOwnQueueMember(queueKey, loggedIn));
                    }}
                    activeColor="bg-pbx-huntgroup"
                    text={i18next.t('pbx.loggedin')}
                />
            }
        />
    );
};

export default QueueOwnMemberLogin;

const updateOwnQueueMember =
    (queueKey: QueueKey, loggedIn: boolean) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const ownExtensionKey = selectOwnExtensionKey(getState());
        const ownQueueMemberKey = selectQueueMembers(getState(), queueKey)
            ?.find((member) => member.get('extensionKey') === ownExtensionKey)
            ?.get('key');

        if (!ownQueueMemberKey) return;

        dispatch(
            updateQueueMember(
                queueKey,
                fromJS({
                    key: ownQueueMemberKey,
                    loggedIn: loggedIn,
                }),
            ),
        );
    };
